import * as React from "react";
import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";
import ActionClose from "@material-ui/icons/Close";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

import { Button, showNotification } from "jazasoft";
import Table, { DefaultRowRenderer, TextInput } from "jazasoft/lib/mui/components/Table";

import { isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { useDeepMemo, useMutation, useSafeState } from "../../../hooks";
import { RollReturnTxnType } from "../../../utils/types";
import { isKnitRoll } from "../../../helpers/rollReturnHelper";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000
  },
  drawerHeader: {
    display: "flex",
    width: 480,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "1.5em"
  },
  marginTop: {
    marginTop: "1.5em"
  }
}));

const RollReturnDrawer = props => {
  const classes = useStyles();
  const { open, txnType, cutOrderId, selectedRolls = [], onDrawerClose, onRefresh } = props;

  const [rows, setRows] = useSafeState([]);
  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  const isReturn = txnType === RollReturnTxnType.RETURN;
  const isKnit = selectedRolls.some(isKnitRoll);
  const title = isReturn ? "Roll Return" : "Roll Missing";

  const sumRows = React.useCallback((rows, dataKey) => rows.reduce((total, row) => total + (Number(row[dataKey]) || 0), 0), []);

  React.useEffect(() => {
    if (open) {
      setRows(
        selectedRolls.map(roll => {
          const availableQty = isKnit ? roll.weight : roll.available;
          return { ...roll, availableQty, returnQty: availableQty, balance: 0, remarks: "" };
        })
      );
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange =
    ({ rowIdx, column }) =>
    event => {
      const value = event.target.value;
      setRows(prevRows => {
        const newRows = [...prevRows];
        const row = { ...newRows[rowIdx], [column.dataKey]: column.dataKey === "remarks" ? value.slice(0, 50) : value };

        //** Auto-derive Balance whenever Return Qty changes: leftover = available - returnQty, floored at 0 (excess) **//
        if (column.dataKey === "returnQty") {
          const qty = Number(value) || 0;
          row.balance = Math.max(toFixed(row.availableQty - qty), 0);
        }

        newRows[rowIdx] = row;
        return newRows;
      });
    };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "F. Roll No." },
      { dataKey: "supplierRollNo", title: "S. Roll No." },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "rollType", title: "Roll Type" },
      { dataKey: "groupName", title: "Group" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "width", title: "Width", element: ({ record }) => (isEmpty(record.width) ? "-" : `${record.width} cm`) },
      {
        dataKey: "length",
        title: isKnit ? " Weight" : " Length",
        element: ({ record }) => (isKnit ? `${toFixedValue(record.weight, 3)} kg` : `${toFixedValue(record.length)} m`)
      },
      {
        dataKey: "availableQty",
        title: isKnit ? "Available Weight" : "Available Length",
        element: ({ record }) => (isKnit ? `${toFixedValue(record.availableWeight, 3)} kg` : `${toFixedValue(record.available)} m`)
      }
    ];

    if (isReturn) {
      cols = [
        ...cols,
        { dataKey: "returnQty", title: "Return Qty", element: <TextInput onChange={onChange} type="number" /> },
        { dataKey: "balance", title: "Balance", element: <TextInput onChange={onChange} type="number" /> }
      ];
    }

    cols.push({ dataKey: "remarks", title: "Remarks", element: <TextInput onChange={onChange} /> });

    return cols;
  }, [isKnit, isReturn]);

  const totalValues = useDeepMemo(
    () => ({
      rollNo: "Total",
      length: sumRows(rows, isKnit ? "weight" : "length"),
      availableQty: sumRows(rows, isKnit ? "availableWeight" : "available"),
      returnQty: sumRows(rows, "returnQty"),
      balance: sumRows(rows, "balance")
    }),
    [rows, isKnit, sumRows]
  );

  const rowsWithTotal = useDeepMemo(() => [...rows, { totalRow: true }], [rows]);

  const setRowsWithoutTotal = React.useCallback(
    nextRows => setRows(nextRows.filter(row => !row.totalRow)),
    [setRows]
  );

  const rowRenderer = React.useCallback(
    ({ classes: tableClasses, columns: tableColumns, record, rowIdx, rowStyle, cellStyle }) => {
      if (!record.totalRow) {
        return React.createElement(DefaultRowRenderer, {
          classes: tableClasses,
          columns: tableColumns,
          record,
          rowIdx,
          rowStyle,
          cellStyle
        });
      }

      return (
        <TableRow
          key={rowIdx}
          style={{
            ...rowStyle,
            fontWeight: 600,
            backgroundColor: "#fafafa"
          }}>
          {tableColumns.map(column => {
            const value = totalValues[column.dataKey];
            const formattedValue = ["length", "availableQty", "returnQty", "balance"].includes(column.dataKey)
              ? `${toFixedValue(value, isKnit ? 3 : 2)} ${isKnit ? "kg" : "m"}`
              : value || "";

            return (
              <TableCell
                className={tableClasses.rowCell}
                key={column.dataKey}
                align={column.align || "left"}
                style={cellStyle ? cellStyle({ record, column }) : {}}>
                {formattedValue}
              </TableCell>
            );
          })}
        </TableRow>
      );
    },
    [isKnit, totalValues]
  );

  const hasInvalidQty =
    isReturn && rows.some(row => isEmpty(row.returnQty) || isNaN(Number(row.returnQty)) || Number(row.returnQty) < 0 || Number(row.balance) < 0);

  const onSubmit = () => {
    if (isEmpty(rows) || hasInvalidQty) return;

    const options = {
      url: `roll-return`,
      method: "post",
      params: { cutOrderId },
      data: {
        txnType,
        rollList: rows.map(row => ({
          id: row.id,
          cuttingRemarks: row.remarks,
          ...(isReturn
            ? {
                ...(isKnit ? { returnedWeight: Number(row.returnQty) } : { returnedLength: Number(row.returnQty) }),
                balanceQty: Number(row.balance)
              }
            : {})
        }))
      }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(isReturn ? "Roll return initiated successfully." : "Roll marked as missing successfully."));
        onRefresh && onRefresh();
        onDrawerClose && onDrawerClose();
      },
      onFailure: () => onDrawerClose && onDrawerClose()
    });
  };

  return (
    <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <div>
          <Typography variant="h5">{title}</Typography>
        </div>
      </div>
      <Divider />

      <div className={classes.content}>
        <Table columns={columns} rows={rowsWithTotal} setRows={setRowsWithoutTotal} rowRenderer={rowRenderer} />

        <div style={{ display: "flex", flex: 1, flexGrow: 1 }} />

        <div className={classes.marginTop}>
          <Button label="Submit" variant="contained" color="primary" disabled={saving || isEmpty(rows) || hasInvalidQty} onClick={onSubmit}>
            <ActionSend />
          </Button>
          <Button label="Cancel" variant="outlined" color="primary" onClick={onDrawerClose} style={{ marginLeft: "1em" }}>
            <ActionClose />
          </Button>
        </div>
      </div>
    </MuiDrawer>
  );
};

export default RollReturnDrawer;
